<template>
  <!-- ===================================================================
       ÉCRAN D'ACCUEIL DE L'APPLICATION — balisage propre, sans héritage.
       ===================================================================
       Le site conserve intégralement son balisage dans le `v-else` plus bas :
       rien ne lui est ajouté, pas même une classe. L'application ne réutilise
       aucune de ses structures, et n'a donc plus à lutter contre sa feuille
       de style.

       Direction : la typographie porte l'écran, pas la photo. Ce n'est pas un
       parti pris gratuit — les visuels d'offres sont servis en 414x219 au
       maximum (le serveur renvoie 404 au-delà, vérifié). Étalés en pleine
       largeur sur un écran en densité double, ils seraient flous. Ils restent
       donc en vignettes de 104px, où ils sont parfaitement nets, et c'est
       Gagalin qui construit la page.
       =================================================================== -->
  <div v-if="isNative" class="ap-home">

    <!-- Le titre reste un h1 pour la structure du document ; le nom de la
         marque vit dans l'attribut alt, pas dans un texte redondant à côté
         du logo. Le fichier est détouré de ses marges transparentes, qui
         mangeaient un tiers de la hauteur du PNG d'origine. -->
    <header class="ap-brand">
      <h1 class="ap-brand__name">
        <img
          class="ap-brand__logo"
          src="/images/logo-app.png"
          alt="OuiCSport"
          width="709"
          height="245"
          loading="eager"
          fetchpriority="high"
        >
      </h1>
    </header>

    <!-- ============ Les sports proposés en ce moment ============ -->
    <!-- La condition porte sur toute la section : sans elle, le titre et son
         sous-titre restaient seuls à l'écran quand aucun sport n'a de date à
         venir. -->
    <section v-if="apSports.length" class="ap-block" aria-labelledby="ap-sports">
      <div class="ap-block__head">
        <h2 id="ap-sports" class="ap-block__title">Sports du moment</h2>
        <p class="ap-block__sub">Réservez votre prochaine session</p>
      </div>

      <div class="ap-cats">
        <button
          type="button"
          class="ap-cat ap-cat--lead"
          @click="selectedFilter = apSports[0].nom"
        >
          <img class="ap-cat__img" :src="apSports[0].image" :alt="''" loading="eager" fetchpriority="high" @error="apBanniereAbsente">
          <span class="ap-cat__veil"></span>
          <span class="ap-cat__foot">
            <span class="ap-cat__name">{{ apSports[0].nom }}</span>
            <span class="ap-cat__count">{{ apSports[0].sessions }} {{ apSports[0].sessions > 1 ? 'sessions' : 'session' }}</span>
          </span>
        </button>

        <div v-if="apSports.length > 1" class="ap-cats__pair">
          <!-- Tous les sports suivants, et non les deux premiers : au-delà de
               trois sports, les autres disparaissaient sans que rien ne
               l'indique. -->
          <button
            v-for="sport in apSports.slice(1)"
            :key="sport.nom"
            type="button"
            class="ap-cat ap-cat--half"
            @click="selectedFilter = sport.nom"
          >
            <img class="ap-cat__img" :src="sport.image" :alt="''" loading="lazy" @error="apBanniereAbsente">
            <span class="ap-cat__veil"></span>
            <span class="ap-cat__foot">
              <span class="ap-cat__name">{{ sport.nom }}</span>
              <span class="ap-cat__count">{{ sport.sessions }}</span>
            </span>
          </button>
        </div>
      </div>
    </section>

    <!-- ============ Les activités du moment ============ -->
    <section class="ap-block" aria-labelledby="ap-sorties">
      <div class="ap-block__head">
        <h2 id="ap-sorties" class="ap-block__title">À ne pas manquer</h2>
        <p class="ap-block__sub">Sorties et séjours à venir</p>
      </div>

      <div class="ap-chips" role="tablist" aria-label="Filtrer les sorties">
        <button
          type="button" role="tab" class="ap-chip"
          :class="{ 'is-on': !selectedFilter && apTypeFilter === 'toutes' }"
          :aria-selected="!selectedFilter && apTypeFilter === 'toutes'"
          @click="selectedFilter = null; apTypeFilter = 'toutes'"
        >Toutes</button>
        <button
          type="button" role="tab" class="ap-chip"
          :class="{ 'is-on': apTypeFilter === 'sessions' }"
          :aria-selected="apTypeFilter === 'sessions'"
          @click="apTypeFilter = 'sessions'"
        >Sessions collectives</button>
        <button
          type="button" role="tab" class="ap-chip"
          :class="{ 'is-on': apTypeFilter === 'sejours' }"
          :aria-selected="apTypeFilter === 'sejours'"
          @click="apTypeFilter = 'sejours'"
        >Stages et séjours</button>
        <button
          v-for="sport in apSports"
          :key="`c-${sport.nom}`"
          type="button" role="tab" class="ap-chip"
          :class="{ 'is-on': selectedFilter === sport.nom }"
          :aria-selected="selectedFilter === sport.nom"
          @click="selectedFilter = sport.nom"
        >{{ sport.nom }}</button>
      </div>

      <div v-if="apSessions.length" class="ap-grid">
        <article
          v-for="s in apSessions"
          :key="s.key"
          class="ap-card"
          :class="{ 'is-full': s.complet }"
        >
          <RouterLink
            class="ap-card__link"
            :to="{ name: 'OfferDetailsPage', params: { id: `${s.id} ${s.date} 00:00` } }"
          >
            <span class="ap-card__photo">
              <img :src="s.image" :alt="''" width="414" height="219" loading="lazy">
              <span v-if="s.type" class="ap-card__type">{{ s.type }}</span>
              <span v-if="s.complet" class="ap-card__full">Complet</span>
            </span>

            <span class="ap-card__body">
              <span class="ap-card__sport">{{ s.sport }}</span>
              <span class="ap-card__title">{{ s.titre }}</span>

              <span class="ap-card__facts">
                <span class="ap-fact">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><rect x="3" y="4.5" width="18" height="17" rx="2.5"/><path d="M16 2.5v4M8 2.5v4M3 10h18"/></svg>
                  {{ s.date }}
                </span>
                <span v-if="s.ville" class="ap-fact">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M20.5 10.5c0 6.5-8.5 12-8.5 12s-8.5-5.5-8.5-12a8.5 8.5 0 0 1 17 0z"/><circle cx="12" cy="10.3" r="2.8"/></svg>
                  {{ s.ville }}
                </span>
              </span>

              <span v-if="s.club" class="ap-card__club">{{ s.club }}</span>

              <span class="ap-card__foot">
                <span v-if="s.niveau" class="ap-card__level" :title="s.niveau">{{ s.niveau }}</span>
                <span v-else class="ap-card__level">Tous niveaux</span>
                <span v-if="s.prix" class="ap-card__price">{{ s.prix }}<span>&nbsp;€</span></span>
              </span>
            </span>
          </RouterLink>
        </article>
      </div>

      <p v-else-if="offers.offers.length" class="ap-void">
        Aucune sortie ne correspond à ce filtre pour le moment.
      </p>

      <div v-else class="ap-grid" aria-hidden="true">
        <span v-for="n in 4" :key="n" class="ap-card ap-card--ghost"></span>
      </div>
    </section>

    <RouterLink to="/OffersList" class="ap-all">
      Tout le catalogue
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M5 12h13M12.5 5.5 19 12l-6.5 6.5" />
      </svg>
    </RouterLink>
  </div>

  <!-- ===== Site web : balisage d'origine, strictement inchangé ===== -->
  <template v-else>
    <main>
        <section class="hero" style="">
            <h1>ACTIVITÉS ET SÉJOURS SPORTIFS EN GROUPE</h1>
        </section>

        <section class="upcoming-sessions">
            <h2>DÉCOUVREZ NOS PROCHAINES SESSIONS</h2>
            <div class="session-cards">
                <div
                    v-for="sport in filteredOffers"
                    :key="sport.sportName"
                    class="session-card"
                >
                <template v-for="item in sport.offerDetails" :key="item.id">
      
                    <router-link
                      v-if="!item.isComplet"
                      :to="{
                        name: 'OfferDetailsPage',
                        params: { id: `${item.id} ${item.nextAvailableDate || item.dates[0]} 00:00` }
                      }"
                      custom
                      v-slot="{ navigate }"
                    >
                      <div @click="navigate" class="session-card-content">
                        <div class="session-card-header">
                          <h3>{{ formatDate(item.nextAvailableDate || item.dates[0]) }}</h3>
                        </div>
                        <img
                          :src="apiImg(item.image.replace('187x97/', '414x219/'))"
                          alt="Image de l'offre"
                        />
                        <span class="session-text-bold">{{ sport.sportName }}</span>
                        <p v-if="item.titleOffers" v-html="item.titleOffers"></p>
                        <p v-else v-html="`${item.type} à ${item.finalCity}`"></p>
                      </div>
                    </router-link>
                    
                    <div
                      v-else
                      class="session-card-content session-card-content--complete"
                    >
                      <div class="session-card-header">
                        <h3>{{ formatDate(item.dates[0]) }}</h3>
                      </div>
                      <img
                        :src="item.image.replace('187x97/', '414x219/')"
                        alt="Image de l'offre"
                      />
                      <span class="session-text-bold">{{ sport.sportName }}</span>
                      <p v-if="item.titleOffers" v-html="item.titleOffers"></p>
                      <p v-else v-html="`${item.type} à ${item.finalCity}`"></p>
                      <!-- badge “Complet” -->
                      <span class="session-complete-label">Complet</span>
                    </div>

                  </template>
                </div>
            </div>
            <div class="voir-plus-container">
                    <router-link to="/OffersList" class="voir-plus-button">
                    Voir plus
                    </router-link>
                </div>
        </section>
    </main>
<!-- Section Descriptions -->
<section class="descriptions">

    <div class="descriptions-text">
        <h2>UN LARGE CHOIX D'ACTIVITÉS SPORTIVES</h2>
        <p>Randonnée, équitation, tennis, vélo, canoë... Du cours découverte d'1h30 au week-end complet, vous trouverez forcément la sortie qui vous intéresse ! Pour couronner le tout, la plupart des sessions proposées sont ouvertes à tous niveaux, idéal pour se découvrir une nouvelle passion, progresser à son rythme ou se surpasser pour les sportifs réguliers.</p>
    </div>

    <div class="descriptions-text">
        <h2>UNE BONNE AMBIANCE ASSURÉE</h2>
        <p>Que vous veniez seul, en couple, en famille ou entre amis, nous vous accueillons à bras ouverts pour partager un bon moment en votre compagnie. Vous hésitez à sauter le pas ? Des soirées de bienvenue sont régulièrement organisées afin de venir à notre rencontre et échanger sur les différentes sorties et séjours... tout en vous imprégnant de l'atmosphère conviviale de OuiCSport !</p>
    </div>

    <div class="descriptions-text">
        <h2>DES PARTENAIRES SÉLECTIONNÉS</h2>
        <p>Afin de vous garantir la meilleure expérience possible, toutes nos activités sont encadrées par des professionnels. Nous sélectionnons rigoureusement les prestataires que nous vous proposons pour une découverte, un entraînement ou un séjour réussi. Professionnalisme, bienveillance, sécurité et adaptabilité sont des indispensables !</p>
    </div>

    <div class="descriptions-text">
        <h2>UNE ORGANISATION AU TOP</h2>
        <p>Pour que vous n'aillez à vous soucier de rien pendant votre activité ou séjour, nos programmes sont conçus de A à Z. De l'organisation des transports en covoiturage ou des navettes depuis une gare, à la flexibilité des repas pour s'adapter à tous les régimes alimentaires... Nous sommes à votre disposition pour répondre à toutes vos questions avant chaque session !</p>
    </div>
</section>

<!-- Section Activités -->
<!-- Pas de `oc-section` ici : la section .activities porte déjà sa propre mise
     en page native (définie dans OffersList.vue, partagée par les deux écrans).
     Cumuler les deux donnait une marge de 32px à gauche du titre contre 16 pour
     les autres sections, un décalage visible. -->
<section class="activities">
    <h2>NOS DIFFÉRENTES ACTIVITÉS</h2>
    <ul class="activity-list">
      
      <li v-for="sport in filteredOffers" :key="sport.sportName" @click="redirectToOffersList(sport.sportName)">
        <img :src="sportBanner(sport.sportName)" :alt="sport.sportName">
        <span>{{ sport.sportName }}</span>
      </li>
    </ul>

</section>


<section class="about-section">
    <p class="accreditation">Agence de voyage agréée par Atout France, OuiCSport propose des sorties et séjours sportifs en groupe, en France et à l'étranger.</p>
    
    <p>Créée en 2019, juste avant le COVID, l'aventure OuiCSport n'a pu réellement débuter qu'après le dernier déconfinement, à l'été 2021. Issus du milieu sportif, nous avons eu envie de faciliter l'accès au sport avec des offres à la carte. Seul ou accompagné, vous pouvez venir découvrir ou redécouvrir l'activité sportive de votre choix, vous entraîner à votre sport préféré, ou tout simplement vous défouler et prendre l'air, le temps d'un après-midi ou d'un week-end entier...</p>

    <p>Nous avons à cœur de vous offrir un moment exceptionnel, des défis sportifs, de belles rencontres. Nous mettons en avant lors de nos différentes sessions, l'esprit d'équipe, la convivialité, le partage, l'entraide, la bienveillance... Faire du sport en se faisant plaisir et en se surpassant, partir à la découverte de nouveaux horizons, de nouvelles passions, s'aérer le corps et l'esprit...</p>

    <p>Nous n'attendons plus que vous pour partir à l'aventure !</p>

    <h2>PROGRAMME SUR-MESURE</h2>

    <p>Des suggestions à nous apporter ? N'hésitez pas à <router-link to="/Contact">nous contacter</router-link> pour nous faire part de vos souhaits. Vous avez envie de découvrir un nouvel endroit ? Une nouvelle activité ? Organiser un team building ou un anniversaire ? Nous sommes à votre écoute pour répondre à toutes vos demandes.</p>
</section>


<section class="gallery">
    <h2>GALERIE</h2>
    <div class="gallery-grid"  >
        <div v-for="image in images" :key="image.src" class="image-container">
            <img :src="image.src" :alt="image.alt" @click="openModal(image.alt)">
        </div>
    </div>
</section>
  </template>
  </template>
  
  <script>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { apiImg, sportBanner, isNative } from '@/api/base';

export default {
    setup() {
        const offers = ref({ sports: [], offers: [] });
        const selectedFilter = ref(null);
        const sportNames = ref([]);
        const images = ref([]);

        async function fetchImages() {
            try {
                const response = await axios.get('/api/gallery/images');
                images.value = response.data
                    .filter(filename => !filename.startsWith('banner_'))
                    .slice(0, 10)
                    .map(filename => ({
                        src: `https://www.ouicsport.fr/api/gallery_img/${filename}`,
                        alt: `${filename}`
                    }));
            } catch (error) {
                console.error("Erreur lors de la récupération des images :", error);
            }
        }

        fetchImages();
        
        const todayAtMidnight = () => {
            const t = new Date();
            t.setHours(0, 0, 0, 0);
            return t;
        };
        const computeDetailSoldOut = async (detail) => {
            try {
                const today = todayAtMidnight().getTime();
                const resp = await axios.get(`https://www.ouicsport.fr/api/search/offer-details-json/${detail.id}`);
                const data = resp?.data?.["0"] ? JSON.parse(resp.data["0"]) : (resp?.data || {});
                const criteria = Array.isArray(data?.criteria) ? data.criteria : [];

                // Regrouper les criteria par date (partie date uniquement, sans heure), en ne gardant que les futurs
                const groupedByDate = {};
                for (const c of criteria) {
                    if (typeof c?.date !== 'string') continue;
                    const datePart = c.date.split(' ')[0];
                    const [d, m, y] = datePart.split('/');
                    const ts = new Date(+y, +m - 1, +d).getTime();
                    if (Number.isNaN(ts) || ts < today) continue;
                    if (!groupedByDate[datePart]) {
                        groupedByDate[datePart] = { ts, crits: [] };
                    }
                    groupedByDate[datePart].crits.push(c);
                }

                const sortedDates = Object.entries(groupedByDate).sort((a, b) => a[1].ts - b[1].ts);

                if (sortedDates.length === 0) {
                    // Aucun criteria futur connu côté back : fallback sur detail.dates (au moins une future ?)
                    const hasFutureDate = (detail.dates || []).some((dateStr) => {
                        const [d, m, y] = dateStr.split('/');
                        return new Date(+y, +m - 1, +d).getTime() >= today;
                    });
                    detail.isComplet = !hasFutureDate;
                    return;
                }

                // Une date est complète ssi AUCUN de ses criteria n'a de place.
                // Le stock peut être négatif quand un créneau a été surréservé
                // (l'API renvoie « -4 ») : comparer à la chaîne '0' faisait
                // passer « -4 » pour une disponibilité, et l'accueil proposait
                // une date entièrement complète.
                const placesDe = (c) => {
                    const n = parseInt(c && c.quantite, 10);
                    return Number.isFinite(n) && n > 0 ? n : 0;
                };
                const firstAvailable = sortedDates.find(([, { crits }]) =>
                    crits.some((c) => placesDe(c) > 0)
                );

                if (firstAvailable) {
                    detail.isComplet = false;
                    detail.nextAvailableDate = firstAvailable[0];
                } else {
                    detail.isComplet = true;
                }
            } catch (e) {
                // En cas d'erreur réseau, fallback optimiste : ne pas marquer complet à tort
                detail.isComplet = false;
            }
        };
        const computeHomepageSoldOutFlags = async () => {
            try {
                // Calculer isComplet + nextAvailableDate pour TOUS les details de TOUS les sports,
                // afin que filteredOffers puisse préférer un detail avec dispo quand dispo.
                const allDetails = (offers.value.offers || []).flatMap((off) => off.offerDetails || []);
                await Promise.all(allDetails.map((det) => computeDetailSoldOut(det)));
            } catch (e) {
                // no-op
            }
        };

        const fetchOffers = async () => {
        try {
            const response = await axios.get(
            'https://www.ouicsport.fr/api/search/by-sport-json'
            );
            const data = JSON.parse(response.data);
            offers.value = {
            sports: data.sports,
            offers: data.offers.map((offer) => ({
                sportName: offer.sportName,
                offerDetails: offer.offerDetails.slice(0, 4), 
            })),
            };

            sportNames.value = offers.value.offers.map((offer) => offer.sportName);
            // Calcul des flags "complet" pour les cartes affichées en page d'accueil
            await computeHomepageSoldOutFlags();
        } catch (error) {
            console.error('Erreur lors de la récupération des offres:', error);
        }
        };

        const filteredOffers = computed(() => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  let offersList = selectedFilter.value
    ? offers.value.offers.filter(
        (offer) => offer.sportName === selectedFilter.value
      )
    : offers.value.offers

  offersList = offersList.filter((offer) => {
    const firstDetail = offer.offerDetails[0]
    if (!firstDetail) return false

    const type = firstDetail.type
    const isStageSejour = [
      'Séjour',
      'Stage à la carte',
      'Stage',
      'Stage/Séjour',
    ].includes(type)

    if (isStageSejour) {
      const hasPast = firstDetail.dates.some((dateStr) => {
        const [day, month, year] = dateStr.split('/')
        const d = new Date(+year, +month - 1, +day)
        return d.getTime() < today.getTime()
      })
      return !hasPast
    }

    return true
  })

  if (offersList.length === 0) return []

  const parseFrDate = (str) => {
    if (!str) return Number.POSITIVE_INFINITY
    const [d, m, y] = str.split('/')
    return new Date(+y, +m - 1, +d).getTime()
  }

  offersList = offersList
    .map((offer) => {
      const upcomingDetails = offer.offerDetails.filter((item) =>
        item.dates.some((dateStr) => {
          const [d, m, y] = dateStr.split('/')
          return new Date(+y, +m - 1, +d).getTime() >= today.getTime()
        })
      )

      // Préférer un detail avec dispo réelle (isComplet === false), trié par nextAvailableDate.
      // Sinon (tous complets OU flags pas encore calculés), fallback sur l'ancien tri par dates[0].
      const availableDetails = upcomingDetails.filter((d) => d.isComplet === false)
      const byNextAvail = (a, b) =>
        parseFrDate(a.nextAvailableDate || a.dates[0]) -
        parseFrDate(b.nextAvailableDate || b.dates[0])

      const chosen =
        availableDetails.slice().sort(byNextAvail)[0] ??
        upcomingDetails.slice().sort((a, b) => parseFrDate(a.dates[0]) - parseFrDate(b.dates[0]))[0]

      return {
        ...offer,
        offerDetails: chosen ? [chosen] : [],
      }
    })
    .filter((offer) => offer.offerDetails.length > 0)
    .sort((a, b) => {
      const parseFirst = (off) => {
        const det = off.offerDetails[0]
        return parseFrDate(det.nextAvailableDate || det.dates[0])
      }
      return parseFirst(a) - parseFirst(b)
    })
    .slice(0, 4)
  return offersList
});





        /* ------------------------------------------------------------------
           Données de l'écran d'accueil natif.
           Le site n'utilise rien de ce qui suit : il garde `filteredOffers`,
           groupé par sport, qui convient à sa mise en page en colonnes.
           ------------------------------------------------------------------ */

        const AUJOURDHUI = () => { const t = new Date(); t.setHours(0,0,0,0); return t.getTime(); };
        const enTemps = (fr) => {
            const [j, m, a] = String(fr).split(' ')[0].split('/').map(Number);
            return new Date(a, m - 1, j).getTime();
        };
        const dateUtile = (d) => d.nextAvailableDate || (d.dates && d.dates[0]) || null;

        /** Filtre par type d'offre, propre à l'app (le site n'en a pas). */
        const apTypeFilter = ref('toutes');

        /** Toutes les offres à venir, tous sports confondus. */
        const apOffresAVenir = computed(() => {
            const seuil = AUJOURDHUI();
            const out = [];
            for (const sport of offers.value.offers || []) {
                for (const d of sport.offerDetails || []) {
                    const date = dateUtile(d);
                    if (!date || enTemps(date) < seuil) continue;
                    out.push({ sport: sport.sportName, d, date });
                }
            }
            return out;
        });

        /**
         * Cartes de catégorie. Le compteur affiché est réel : c'est le nombre
         * de sessions à venir du sport, pas une valeur décorative.
         * La plus fournie passe en grande carte, les suivantes en demi-largeur.
         */
        /**
         * Les bannières de sport sont servies par convention de nom
         * (`banner_<Sport>.png`). Un sport ajouté sans sa bannière affichait
         * l'icône de fichier cassé au milieu de la carte : on masque l'image,
         * la carte reste lisible puisque son fond est déjà sombre.
         */
        const apBanniereAbsente = (e) => { e.target.style.visibility = 'hidden'; };

        const apSports = computed(() => {
            const parSport = new Map();
            for (const o of apOffresAVenir.value) {
                parSport.set(o.sport, (parSport.get(o.sport) || 0) + 1);
            }
            return [...parSport.entries()]
                .map(([nom, sessions]) => ({
                    nom,
                    sessions,
                    image: sportBanner(nom),
                }))
                .sort((a, b) => b.sessions - a.sessions);
        });

        const TYPES_SEJOUR = ['Stage', 'Séjour', 'Stage à la carte', 'Stage/Séjour'];

        /** Cartes d'activité, triées par date, filtrées par sport et par type. */
        const apSessions = computed(() => {
            const rows = apOffresAVenir.value
                .filter((o) => !selectedFilter.value || o.sport === selectedFilter.value)
                .filter((o) => {
                    const t = o.d.type;
                    if (apTypeFilter.value === 'sessions') return !TYPES_SEJOUR.includes(t);
                    if (apTypeFilter.value === 'sejours') return TYPES_SEJOUR.includes(t);
                    return true;
                })
                .map(({ sport, d, date }) => {
                    // Le niveau vit dans les critères de l'offre, sous l'intitulé
                    // « Niveaux » ; absent sur beaucoup d'offres, d'où le repli.
                    let niveau = null;
                    for (const c of d.criteria || []) {
                        const v = (c.criterionValues || []).find((x) => x.name === 'Niveaux');
                        if (v && v.value) { niveau = v.value; break; }
                    }
                    return {
                        key: `${d.id}-${date}`,
                        id: d.id,
                        date,
                        sport,
                        type: d.type,
                        titre: d.titleOffers || `${d.type} à ${d.finalCity}`,
                        ville: d.finalCity,
                        club: d.clubName,
                        prix: d.formatedPrice,
                        niveau,
                        image: apiImg(String(d.image || '').replace('187x97/', '414x219/')),
                        complet: !!d.isComplet,
                    };
                });
            return rows.sort((a, b) => enTemps(a.date) - enTemps(b.date));
        });

        onMounted(fetchOffers);
        return { offers, sportNames, filteredOffers, apSports, apSessions, apTypeFilter, apBanniereAbsente,
                 selectedFilter, images, apiImg, sportBanner, isNative };
    },
    methods: {
        redirectToOffersList(sport) {
            this.$router.push({ path: '/OffersList', query: { sport: sport } });
        },
        formatDate(dateStr) {
            const [day, month, year] = dateStr.split('/').map(Number);
            const date = new Date(year, month - 1, day);
            const options = { day: 'numeric', month: 'long', year: 'numeric' };
            return date.toLocaleDateString('fr-FR', options);
        }
    }
};
</script>

<style>
/* =========================================================================
   ACCUEIL DE L'APPLICATION — feuille écrite de zéro
   =========================================================================
   Le balisage natif est distinct de celui du site (voir le <template>), donc
   rien n'est hérité ni combattu. Tous les sélecteurs restent préfixés
   `body.is-native` : le contrôle automatique vérifie ainsi que le site n'est
   pas touché.

   Palette crème chaude, jamais de blanc ni de noir purs. Le rouge de marque
   sert d'accent structurant : sport, filtre actif, badge « complet ».

   Contrainte matérielle qui commande la grille : les photos d'offres sont
   plafonnées à 414x219 par le serveur (404 au-delà, vérifié). En deux
   colonnes, leur conteneur mesure ~157px de large — elles y sont donc nettes
   en densité double. Une carte pleine largeur les rendrait floues.
   Les visuels de sport, eux, existent en 1920x1080 : ils peuvent dominer.
   ========================================================================= */

body.is-native .ap-home,
body.is-native .ap-home *,
body.is-native .ap-home *::before,
body.is-native .ap-home *::after {
  box-sizing: border-box;
}

/* Remise à zéro au niveau des ÉLÉMENTS. Indispensable : la feuille du site
   cible aussi des éléments nus — `header` y est peint en blanc, `h1`, `h2`,
   `button`, `img`, `p` ont leurs propres réglages, et ils rattrapaient mes
   éléments malgré un balisage neuf. */
body.is-native .ap-home header,
body.is-native .ap-home section,
body.is-native .ap-home article,
body.is-native .ap-home h1,
body.is-native .ap-home h2,
body.is-native .ap-home p,
body.is-native .ap-home span,
body.is-native .ap-home button,
body.is-native .ap-home a,
body.is-native .ap-home img {
  all: unset;
  display: revert;
  /* `all: unset` retire le `overflow: clip` que le navigateur applique
     aux éléments remplacés : sans lui, le débordement d'`object-fit:
     cover` se peint HORS du cadre de l'image. */
  overflow: clip;
  box-sizing: border-box;
}

body.is-native .ap-home {
  --ap-bg: #F5F0E6;
  --ap-surface: #EFE8DA;
  --ap-raised: #E5DDD0;
  --ap-dark: #2B231F;
  --ap-ink: #28201C;
  --ap-ink-2: #6B5E54;
  --ap-ink-3: #8C7E73;
  --ap-on-dark: #FAF6EE;
  --ap-red: #DD4124;
  --ap-hair: rgba(107, 94, 84, 0.14);

  padding-top: calc(env(safe-area-inset-top, 0px) + 10px);
  padding-bottom: var(--oc-sp-6);
  background: var(--ap-bg);
  font-family: 'Dosis', system-ui, sans-serif;
  color: var(--ap-ink);
  -webkit-user-select: none;
  user-select: none;
}

/* ---------- Marque ---------- */

body.is-native .ap-home .ap-brand {
  padding: 10px 20px 4px;
}

body.is-native .ap-home .ap-brand__name {
  display: block;
  /* Le h1 n'enveloppe plus qu'une image : sans cela son interligne ajoute
     quelques pixels vides sous le logo. */
  line-height: 0;
}

body.is-native .ap-home .ap-brand__logo {
  display: block;
  /* La largeur mène, la hauteur suit le rapport du fichier détouré
     (709 × 245), pour qu'un changement de logo ne le déforme jamais. */
  width: 152px;
  height: auto;
}

/* ---------- En-tête de bloc ---------- */

body.is-native .ap-home .ap-block {
  margin-top: 22px;
}

body.is-native .ap-home .ap-block__head {
  padding: 0 20px 12px;
}

body.is-native .ap-home .ap-block__title {
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 28px;
  line-height: 1.05;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--ap-ink);
}

body.is-native .ap-home .ap-block__sub {
  margin-top: 3px;
  font-size: 16px;
  font-weight: 600;
  color: var(--ap-ink-2);
}

/* ---------- Catégories : l'image domine ---------- */

body.is-native .ap-home .ap-cats {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 20px;
}

body.is-native .ap-home .ap-cats__pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

body.is-native .ap-home .ap-cat {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  padding: 14px;
  overflow: hidden;
  border-radius: 20px;
  background: var(--ap-dark);
  cursor: pointer;
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .ap-home .ap-cat--lead { height: 180px; }
body.is-native .ap-home .ap-cat--half { height: 130px; border-radius: 18px; }

/* Une carte seule sur sa ligne occupe les deux colonnes : sinon, avec un
   nombre pair de sports, la dernière restait collée à gauche et laissait un
   vide béant à droite. */
body.is-native .ap-home .ap-cats__pair > .ap-cat--half:last-child:nth-child(odd) {
  grid-column: 1 / -1;
}

body.is-native .ap-home .ap-cat:active { transform: scale(0.98); }

body.is-native .ap-home .ap-cat__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.86);
}

body.is-native .ap-home .ap-cat__veil {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(20, 16, 14, 0) 22%, rgba(20, 16, 14, 0.86) 100%);
}

body.is-native .ap-home .ap-cat__foot {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
}

body.is-native .ap-home .ap-cat__name {
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 32px;
  line-height: 0.95;
  letter-spacing: 0.5px;
  color: var(--ap-on-dark);
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
}

body.is-native .ap-home .ap-cat--half .ap-cat__name { font-size: 22px; }

body.is-native .ap-home .ap-cat__count {
  flex: 0 0 auto;
  padding: 5px 11px;
  font-size: 15px;
  font-weight: 700;
  color: var(--ap-ink);
  background: rgba(245, 240, 230, 0.93);
  border-radius: 20px;
}

body.is-native .ap-home .ap-cat--half .ap-cat__count {
  padding: 3px 9px;
  font-size: 14px;
}

/* ---------- Filtres ---------- */

body.is-native .ap-home .ap-chips {
  display: flex;
  gap: 8px;
  padding: 0 20px 14px;
  overflow-x: auto;
  scroll-padding-left: 20px;
  scrollbar-width: none;
}

body.is-native .ap-home .ap-chips::-webkit-scrollbar { display: none; }

body.is-native .ap-home .ap-chip {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 700;
  white-space: nowrap;
  color: var(--ap-ink-2);
  background: var(--ap-surface);
  border: 1px solid var(--ap-hair);
  border-radius: 22px;
  cursor: pointer;
  transition: background 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
              color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
              transform 140ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .ap-home .ap-chip.is-on {
  color: var(--ap-on-dark);
  background: var(--ap-red);
  border-color: var(--ap-red);
}

body.is-native .ap-home .ap-chip:active { transform: scale(0.95); }

/* ---------- Cartes d'activité ---------- */

body.is-native .ap-home .ap-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  padding: 0 20px;
}

body.is-native .ap-home .ap-card {
  display: flex;
  overflow: hidden;
  background: var(--ap-surface);
  border: 1px solid var(--ap-hair);
  border-radius: 18px;
  transition: transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .ap-home .ap-card:active { transform: translateY(2px) scale(0.98); }
body.is-native .ap-home .ap-card.is-full { opacity: 0.86; }

body.is-native .ap-home .ap-card__link {
  display: flex;
  flex-direction: column;
  width: 100%;
  cursor: pointer;
}

body.is-native .ap-home .ap-card__photo {
  position: relative;
  display: block;
  width: 100%;
  height: 110px;
  overflow: hidden;
  background: var(--ap-raised);
}

body.is-native .ap-home .ap-card__photo img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

body.is-native .ap-home .ap-card__type,
body.is-native .ap-home .ap-card__full {
  position: absolute;
  top: 8px;
  display: block;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--ap-on-dark);
  border-radius: 8px;
  max-width: calc(100% - 16px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

body.is-native .ap-home .ap-card__type {
  left: 8px;
  background: rgba(43, 35, 31, 0.84);
}

body.is-native .ap-home .ap-card__full {
  right: 8px;
  background: var(--ap-red);
  font-weight: 800;
}

body.is-native .ap-home .ap-card__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 12px;
}

body.is-native .ap-home .ap-card__sport {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: var(--ap-red);
}

body.is-native .ap-home .ap-card__title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--ap-ink);
}

body.is-native .ap-home .ap-card__facts {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

body.is-native .ap-home .ap-fact {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 600;
  color: var(--ap-ink-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

body.is-native .ap-home .ap-fact svg { flex: 0 0 auto; }

body.is-native .ap-home .ap-card__club {
  display: block;
  margin-top: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--ap-ink-3);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

body.is-native .ap-home .ap-card__foot {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid rgba(107, 94, 84, 0.12);
}

body.is-native .ap-home .ap-card__level {
  min-width: 0;
  padding: 3px 7px;
  font-size: 12px;
  font-weight: 600;
  color: var(--ap-ink-2);
  background: var(--ap-raised);
  border-radius: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

body.is-native .ap-home .ap-card__price {
  flex: 0 0 auto;
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
  color: var(--ap-ink);
  /* Chiffres de même largeur : les prix s'alignent d'une carte à l'autre. */
  font-variant-numeric: tabular-nums;
}

body.is-native .ap-home .ap-card__price span {
  font-size: 14px;
  font-weight: 600;
}

/* ---------- Chargement, vide, sortie ---------- */

body.is-native .ap-home .ap-card--ghost {
  display: block;
  height: 240px;
  border: 0;
  background: linear-gradient(90deg, var(--ap-surface) 25%, var(--ap-raised) 37%, var(--ap-surface) 63%);
  background-size: 400% 100%;
  animation: ap-shimmer 1.4s ease infinite;
}

@keyframes ap-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

body.is-native .ap-home .ap-void {
  padding: 32px 20px;
  font-size: 16px;
  line-height: 1.6;
  color: var(--ap-ink-2);
}

body.is-native .ap-home .ap-all {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 26px 20px 0;
  min-height: 56px;
  padding: 0 18px;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ap-on-dark);
  background: var(--ap-dark);
  border-radius: 18px;
  cursor: pointer;
  transition: transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .ap-home .ap-all:active { transform: scale(0.98); }

@media (prefers-reduced-motion: reduce) {
  body.is-native .ap-home .ap-card--ghost { animation: none; }
}
</style>
